import React, { useState, useEffect } from 'react';
import { CloudRain, Sun, Plus, Check, CalendarDays, RotateCcw } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { careItemsApi } from './encountersApi';

/**
 * Stressings and blessings — what is going on in someone's life right now.
 *
 * They belong to the person, not to a conversation, so this card lives on the
 * About tab (the prayer lens) and outlives any one conversation. The same open
 * list shows up on their prayer calendar card, in the sample prayer, on the
 * plan screen and in the live guide's "before you start" — the point of
 * writing "Dad's surgery is on the 12th" is that you ask about it on the 13th.
 *
 * Open items are present tense and editable here. A closed one ("how did it
 * turn out?") is a record, so it moves to History — the tense rule the person
 * tabs already follow.
 *
 * Text is plain, rendered as a text child.
 */

/** "YYYY-MM-DD" → "Nov 12", formatted in UTC so it can never slide a day west. */
export function formatCareDay(key, opts = { month: 'short', day: 'numeric' }) {
  if (!key) return '';
  const d = new Date(`${String(key).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { ...opts, timeZone: 'UTC' });
}

export function CareKindIcon({ kind, size = 15 }) {
  return kind === 'blessing'
    ? <Sun size={size} strokeWidth={2} aria-hidden="true" />
    : <CloudRain size={size} strokeWidth={2} aria-hidden="true" />;
}

/**
 * A read-only line, for the places that only need to show the list: the plan
 * screen's "ask about", the guide's "before you start", the debrief.
 */
export function CareItemLine({ item, t }) {
  return (
    <li className={`ci-line is-${item.kind}`}>
      <span className="ci-kind"><CareKindIcon kind={item.kind} size={13} />
        <span className="ci-kind-label">{t(`people.care.${item.kind}`)}</span></span>
      <span className="ci-text">{item.text}</span>
      {item.happensOn && <span className="ci-date">{formatCareDay(item.happensOn)}</span>}
    </li>
  );
}

export default function CareItemsCard({ person, items: itemsIn, onChanged }) {
  const { t } = useTranslation();
  const [items, setItems]   = useState(itemsIn || []);
  const [adding, setAdding] = useState(false);
  const [kind, setKind]     = useState('stressing');
  const [text, setText]     = useState('');
  const [day, setDay]       = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');
  const [note, setNote]     = useState('');
  const [closingId, setClosingId] = useState(null);
  const [outcome, setOutcome]     = useState('');

  useEffect(() => { setItems(itemsIn || []); }, [itemsIn]);

  const open = items.filter(i => i.status === 'open');
  const showForm = adding || open.length === 0;

  async function add() {
    const value = text.trim();
    if (!value || busy) return;
    setBusy(true); setError(''); setNote('');
    try {
      const d = await careItemsApi.create(person.id, { kind, text: value, happensOn: day || null });
      if (d?.item) setItems(list => [d.item, ...list]);
      setNote(d?.calendarEventId ? t('people.care.addedToCalendar') : '');
      setText(''); setDay(''); setAdding(false);
      onChanged && onChanged();
    } catch (e) { setError(e.message || t('common.somethingWrong')); }
    setBusy(false);
  }

  async function close(item) {
    setBusy(true); setError('');
    try {
      const d = await careItemsApi.close(item.id, outcome.trim());
      setItems(list => list.map(i => i.id === item.id ? (d?.item || { ...i, status: 'closed' }) : i));
      setClosingId(null); setOutcome('');
      onChanged && onChanged();
    } catch (e) { setError(e.message || t('common.somethingWrong')); }
    setBusy(false);
  }

  async function remove(item) {
    if (!window.confirm(t('people.care.removeConfirm'))) return;
    setBusy(true); setError('');
    try {
      await careItemsApi.remove(item.id);
      setItems(list => list.filter(i => i.id !== item.id));
      setClosingId(null);
      onChanged && onChanged();
    } catch (e) { setError(e.message || t('common.somethingWrong')); }
    setBusy(false);
  }

  return (
    <section className="pa-card ci-card" aria-labelledby="ci-h">
      <h2 className="pa-card-h" id="ci-h">{t('people.care.title')}</h2>
      <p className="dm-hint">{t('people.care.hint')}</p>

      {open.length > 0 ? (
        <ul className="ci-list">
          {open.map(item => (
            <li key={item.id} className={`ci-item is-${item.kind}`}>
              <div className="ci-item-main">
                <span className="ci-kind"><CareKindIcon kind={item.kind} />
                  <span className="ci-kind-label">{t(`people.care.${item.kind}`)}</span></span>
                <span className="ci-text">{item.text}</span>
                {item.happensOn && (
                  <span className="ci-date"><CalendarDays size={12} aria-hidden="true" /> {formatCareDay(item.happensOn)}</span>
                )}
                {closingId !== item.id && (
                  <button type="button" className="ci-close-btn" disabled={busy}
                    onClick={() => { setClosingId(item.id); setOutcome(''); }}>
                    <Check size={14} /> {t('people.care.close')}
                  </button>
                )}
              </div>

              {closingId === item.id && (
                <div className="ci-closing">
                  <label className="dm-label" htmlFor={`ci-out-${item.id}`}>{t('people.care.closeTitle')}</label>
                  <textarea id={`ci-out-${item.id}`} className="dm-textarea" rows={2}
                    value={outcome} placeholder={t('people.care.outcomePh')}
                    onChange={e => setOutcome(e.target.value)} />
                  <div className="ci-closing-actions">
                    <button type="button" className="btn btn-primary ci-save" disabled={busy}
                      onClick={() => close(item)}>{t('common.save')}</button>
                    <button type="button" className="ci-link" disabled={busy}
                      onClick={() => setClosingId(null)}>{t('common.cancel')}</button>
                    <button type="button" className="ci-link ci-danger" disabled={busy}
                      onClick={() => remove(item)}>{t('people.care.remove')}</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : !showForm ? null : (
        <p className="ci-empty">{t('people.care.none', person?.name || '')}</p>
      )}

      {showForm ? (
        <div className="ci-form">
          <div className="ci-kinds" role="group" aria-label={t('people.care.kindLabel')}>
            {['stressing', 'blessing'].map(k => (
              <button key={k} type="button" aria-pressed={kind === k}
                className={`ci-kind-btn is-${k}${kind === k ? ' active' : ''}`}
                onClick={() => setKind(k)}>
                <CareKindIcon kind={k} /> {t(`people.care.${k}`)}
              </button>
            ))}
          </div>
          <input className="ep-input ci-input" value={text} maxLength={300}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder={t(`people.care.textPh.${kind}`)}
            aria-label={t(`people.care.${kind}`)} />
          <label className="ci-date-field">
            <span>{t('people.care.dateLabel')}</span>
            <input type="date" className="ep-input ci-date-input" value={day}
              onChange={e => setDay(e.target.value)} />
          </label>
          <p className="dm-hint ci-date-hint">{t('people.care.dateHint')}</p>
          <div className="ci-form-actions">
            <button type="button" className="btn btn-primary ci-add"
              disabled={busy || !text.trim()} onClick={add}>
              <Plus size={16} /> {t('people.care.add')}
            </button>
            {adding && open.length > 0 && (
              <button type="button" className="ci-link" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <button type="button" className="ci-add-more" onClick={() => setAdding(true)}>
          <Plus size={15} /> {t('people.care.addOne')}
        </button>
      )}

      {note  && <p className="ci-note" role="status">{note}</p>}
      {error && <p className="ep-error" role="alert">{error}</p>}
    </section>
  );
}

/** History's record of the ones that have passed, with how they turned out. */
export function PastCareItems({ items, onReopen }) {
  const { t } = useTranslation();
  const closed = (items || []).filter(i => i.status === 'closed');
  if (!closed.length) return null;
  return (
    <section className="ph-section">
      <h2 className="pa-card-h ph-h">{t('people.care.pastTitle')}</h2>
      <ul className="ci-past">
        {closed.map(item => (
          <li key={item.id} className={`ci-past-item is-${item.kind}`}>
            <span className="ci-kind"><CareKindIcon kind={item.kind} size={13} />
              <span className="ci-kind-label">{t(`people.care.${item.kind}`)}</span></span>
            <span className="ci-text">{item.text}</span>
            {item.outcome && <span className="ci-outcome">{item.outcome}</span>}
            <span className="ci-past-meta">
              {item.closedAt && new Date(item.closedAt).toLocaleDateString(undefined,
                { month: 'short', day: 'numeric', year: 'numeric' })}
              {onReopen && (
                <button type="button" className="ci-link" onClick={() => onReopen(item)}>
                  <RotateCcw size={12} /> {t('people.care.reopen')}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
